import { setPref } from "@common/preferences";

// How the on-screen pad moves the game: a D-pad of four buttons, a drag
// stick whose middle is turbo (hold still there to fast-forward), or a plain
// stick with turbo as its own button. Remembered per browser.

export type Movement = "dpad" | "stick-turbo" | "stick";

export const MOVEMENT_OPTIONS: { value: Movement; label: string }[] = [
  { value: "dpad", label: "D-pad" },
  { value: "stick-turbo", label: "Stick, turbo in the middle" },
  { value: "stick", label: "Stick" },
];

const KEY = "pocket-play.movement";
const SHOULDERS_KEY = "pocket-play.shoulders.";

/**
 * Which cartridges share an L and R setting: Game Boy and Game Boy Color
 * have no L or R, so theirs starts hidden; Game Boy Advance's starts shown.
 */
export type CartridgeFamily = "gb" | "gba";

/** Whether the on-screen L and R buttons are shown for this kind of cartridge. */
export function loadShoulders(cartridge: CartridgeFamily): boolean {
  try {
    const saved = localStorage.getItem(SHOULDERS_KEY + cartridge);
    return saved === null ? cartridge === "gba" : saved === "true";
  } catch {
    return cartridge === "gba";
  }
}

export function saveShoulders(cartridge: CartridgeFamily, shown: boolean) {
  setPref(SHOULDERS_KEY + cartridge, String(shown));
}

/**
 * An N64 game's movement controls, each its own choice per game (games
 * differ in which they use): one of ours (MOVEMENT_OPTIONS), EmulatorJS's
 * own control ("native"), or none (N64_DEFAULT says which each starts as).
 */
export type N64Move = Movement | "native" | "hidden";
export interface N64Controls {
  stick: N64Move;
  dpad: N64Move;
}

export const N64_MOVE_OPTIONS: { value: N64Move; label: string }[] = [
  { value: "native", label: "N64 pad's own" },
  ...MOVEMENT_OPTIONS,
  { value: "hidden", label: "Hidden" },
];

const N64_KEY = "pocket-play.n64Move.";
// Most N64 games never touch the D-pad, so it starts hidden; the stick is
// the N64 pad's own until a game says otherwise.
export const N64_DEFAULT: N64Controls = { stick: "native", dpad: "hidden" };
const isN64Move = (value: unknown): value is N64Move => N64_MOVE_OPTIONS.some((o) => o.value === value);

export function loadN64Controls(game: string): N64Controls {
  try {
    const saved = JSON.parse(localStorage.getItem(N64_KEY + game) ?? "{}");
    return {
      stick: isN64Move(saved.stick) ? saved.stick : N64_DEFAULT.stick,
      dpad: isN64Move(saved.dpad) ? saved.dpad : N64_DEFAULT.dpad,
    };
  } catch {
    return N64_DEFAULT;
  }
}

export function saveN64Controls(game: string, controls: N64Controls) {
  const plain = controls.stick === N64_DEFAULT.stick && controls.dpad === N64_DEFAULT.dpad;
  setPref(N64_KEY + game, plain ? null : JSON.stringify(controls));
}

export function loadMovement(): Movement {
  try {
    const saved = localStorage.getItem(KEY);
    return MOVEMENT_OPTIONS.some((o) => o.value === saved) ? (saved as Movement) : "dpad";
  } catch {
    return "dpad";
  }
}

export function saveMovement(movement: Movement) {
  try {
    setPref(KEY, movement);
  } catch {
    // Kept for this page at least.
  }
}

/** Directions for a digital target: past halfway on an axis presses it. */
export function directions(x: number, y: number) {
  const past = 0.45;
  return { right: x > past, left: x < -past, down: y > past, up: y < -past };
}
